import { tracked } from '@glimmer/tracking';
import { addTime } from 'my-phorest/utils/local-time-helpers';

export class BookingIntent {
  appointmentPlanner;
  selectedPackage;
  type;

  @tracked _staffRequested = true;
  @tracked service;
  @tracked selectedPackageOptions;
  @tracked staffMember;
  @tracked _clientCourseItem;
  @tracked _clientCourse;

  constructor(
    { type, service, selectedPackage, selectedPackageOptions },
    appointmentPlannerService
  ) {
    this.appointmentPlanner = appointmentPlannerService;
    this.type = type;

    if (type === BOOKING_INTENT_TYPE.PACKAGE) {
      this.selectedPackage = selectedPackage;
      this.setPackageOptions(selectedPackageOptions);
    } else {
      this.service = service;
    }
  }

  get isVisibleInPlanner() {
    return this.appointmentPlanner.bookingIntents.indexOf(this) > -1;
  }

  get durationMins() {
    return this.service?.durationMins;
  }

  get finishDurationMins() {
    return this.service?.finishDurationMins || 0;
  }

  get hasStaffMember() {
    return (
      typeof this.staffMember !== 'undefined' &&
      this.staffMember !== ANYONE_QUALIFIED_STAFF_MEMBER
    );
  }

  get price() {
    if (this.type === BOOKING_INTENT_TYPE.COURSE) {
      return 0;
    } else {
      return this.service?.price;
    }
  }

  get staffRequested() {
    const canBeRequested =
      !this.appointmentPlanner.isSearchingInOtherBranch && this.hasStaffMember;

    if (canBeRequested) {
      return this._staffRequested;
    } else {
      return false;
    }
  }
  set staffRequested(value) {
    this._staffRequested = value;
  }

  #getServiceScheduleFromSlots(slots, serviceId, { fromPickedStartTime }) {
    let nextSlots = slots;
    let serviceSchedule;
    if (fromPickedStartTime) {
      serviceSchedule = nextSlots.find(
        (s) =>
          s.serviceId === serviceId &&
          s.startTime === this.appointmentPlanner.startTime
      );
      nextSlots = nextSlots.slice(nextSlots.indexOf(serviceSchedule) + 1);
    } else {
      serviceSchedule = nextSlots.find((s) => s.serviceId === serviceId);
    }
    return {
      nextSlots,
      ...serviceSchedule,
    };
  }

  #serializeAsPackage(availableTimesForDate) {
    const isFirstIntent =
      this.appointmentPlanner.bookingIntents.indexOf(this) === 0;
    let nextAvailableTimesForDate = availableTimesForDate;
    let variables = {
      packageOptions: [],
    };

    this.selectedPackageOptions.forEach((option, index) => {
      let isFirstIntentAndOption = isFirstIntent && index === 0;
      let { startTime, staffMemberId, nextSlots } =
        this.#getServiceScheduleFromSlots(
          nextAvailableTimesForDate,
          option.serviceId,
          { fromPickedStartTime: isFirstIntentAndOption }
        );
      let packageOption = option.getGqlVariables(startTime, staffMemberId);
      variables.packageOptions.push(packageOption);
      nextAvailableTimesForDate = nextSlots;
    });

    return {
      ...variables,
      nextAvailableTimesForDate,
    };
  }

  #serializeAsServiceOrCourse(availableTimesForDate, serviceId) {
    const isFirstIntent =
      this.appointmentPlanner.bookingIntents.indexOf(this) === 0;
    let nextAvailableTimesForDate = availableTimesForDate;
    let variables = {};
    let { startTime, staffMemberId, nextSlots } =
      this.#getServiceScheduleFromSlots(nextAvailableTimesForDate, serviceId, {
        fromPickedStartTime: isFirstIntent,
      });
    variables.selectedTime = startTime;
    variables.staffMemberId = staffMemberId;
    nextAvailableTimesForDate = nextSlots;
    return {
      ...variables,
      nextAvailableTimesForDate,
    };
  }

  getGqlVariables(availableTimesForDate, serviceId) {
    if (this.isPackage) {
      return this.#serializeAsPackage(availableTimesForDate);
    } else if (this.isService || this.isCourse) {
      return this.#serializeAsServiceOrCourse(availableTimesForDate, serviceId);
    }
  }

  setStaffMember(staffMember, { packageOptionId } = {}) {
    if (this.type === BOOKING_INTENT_TYPE.PACKAGE) {
      if (packageOptionId) {
        let option = this.selectedPackageOptions.find(
          (option) => option.id === packageOptionId
        );
        option.staffMember = staffMember;
      } else {
        this.selectedPackageOptions.forEach((option) => {
          option.staffMember = staffMember;
        });
      }
    } else {
      this.staffMember = staffMember;
    }
  }

  setPackageOptions(options) {
    this.selectedPackageOptions = options.map((option, index) => {
      let previousStaff = this.selectedPackageOptions?.[index]?.staffMember;
      return new PackageOptionIntent(option, this, previousStaff);
    });
  }

  setAsCourse(clientCourseItem, clientCourse) {
    this.type = BOOKING_INTENT_TYPE.COURSE;
    this.service = clientCourseItem.chainService;
    this._clientCourseItem = clientCourseItem;
    this._clientCourse = clientCourse;
    return this;
  }

  setAsService(service) {
    this.type = BOOKING_INTENT_TYPE.SERVICE;
    this.service = service;
    return this;
  }

  setAsPackage(selectedPackage, packageOptions) {
    this.type = BOOKING_INTENT_TYPE.PACKAGE;
    this.selectedPackage = selectedPackage;
    this.setPackageOptions(packageOptions);
    return this;
  }

  get clientCourseItem() {
    if (this.type === BOOKING_INTENT_TYPE.COURSE) {
      return this._clientCourseItem;
    }
    return null;
  }

  get clientCourse() {
    if (this.type === BOOKING_INTENT_TYPE.COURSE) {
      return this._clientCourse;
    }
    return null;
  }

  get hasManyStaffMembers() {
    if (this.type !== BOOKING_INTENT_TYPE.PACKAGE) {
      return false;
    } else {
      return (
        new Set(
          this.selectedPackageOptions.map((option) => option.staffMember.id)
        ).size > 1
      );
    }
  }

  get isService() {
    return this.type === BOOKING_INTENT_TYPE.SERVICE;
  }

  get isPackage() {
    return this.type === BOOKING_INTENT_TYPE.PACKAGE;
  }

  get isCourse() {
    return this.type === BOOKING_INTENT_TYPE.COURSE;
  }

  get allStaffMemberContexts() {
    if (this.isPackage) {
      return this.selectedPackageOptions.map((option) => {
        if (option.staffMember) {
          return {
            categoryId: option.staffMember.categoryId,
            userId: option.staffMember.userId,
            name: option.staffMember.name,
          };
        }
        return null;
      });
    } else {
      if (!this.staffMember) return null;
      return {
        categoryId: this.staffMember.categoryId,
        userId: this.staffMember.userId,
        name: this.staffMember.name,
      };
    }
  }
}

export class PackageOptionIntent {
  _bookingIntent;

  @tracked _option;
  @tracked _staffMember;
  @tracked _staffRequested = false;

  constructor(option, bookingIntentParent, staffMember) {
    this._option = option;
    this._bookingIntent = bookingIntentParent;
    if (staffMember || staffMember === ANYONE_QUALIFIED_STAFF_MEMBER) {
      this.staffMember = staffMember;
    }
  }

  get durationMins() {
    return this._option.duration;
  }

  get finishDurationMins() {
    return this._option.finishDuration || 0;
  }

  get hasStaffMember() {
    return (
      typeof this.staffMember !== 'undefined' &&
      this.staffMember !== ANYONE_QUALIFIED_STAFF_MEMBER
    );
  }

  get id() {
    return this._option.id;
  }

  get option() {
    return this._option;
  }
  set option(value) {
    this._option = value;
  }

  get price() {
    return this._option.price;
  }

  get service() {
    return this._option;
  }

  get serviceGroupId() {
    return this._bookingIntent.selectedPackage.id;
  }

  get serviceGroupItemOptionId() {
    return this._option.id;
  }

  get serviceGroupOptionId() {
    return this._option.serviceGroupOptionId;
  }

  get serviceId() {
    return this._option.serviceId;
  }

  get staffMember() {
    return this._staffMember;
  }

  set staffMember(value) {
    this._staffMember = value;
    this.staffRequested = !!value;
  }

  get staffRequested() {
    return this._staffRequested;
  }

  set staffRequested(value) {
    this._staffRequested = value;
  }

  getGqlVariables(requestedStartTime, requestedStaffId) {
    return {
      startTime: requestedStartTime,
      endTime: addTime(requestedStartTime, this.durationMins),
      serviceId: this.serviceId,
      serviceGroupItemOptionId: this.serviceGroupItemOptionId,
      staffRequested: this.staffRequested,
      staffId: requestedStaffId,
    };
  }
}

export const BOOKING_INTENT_TYPE = {
  PACKAGE: 'PACKAGE',
  SERVICE: 'SERVICE',
  COURSE: 'COURSE',
};

export const PLANNER_ROUTES = {
  AVAILABILITY: 'planner.availability',
  BOOKING_HISTORY: 'planner.booking-history',
  CLIENTS: 'planner.clients',
  EDIT_BOOKING_INTENT: 'planner.edit-booking-intent',
  SERVICES: 'planner.services',
  STAFF: 'planner.staff',
};

export const ANYONE_QUALIFIED_STAFF_MEMBER = null;
